import React from 'react';
import { Card, CardMedia, CardContent, Typography, Box } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/currency';
import styles from './EventCard.module.css';

// S3 bucket base URL from environment variables
const S3_BUCKET_URL = process.env.REACT_APP_S3_BUCKET_URL || 'https://myeventshop-images.s3.amazonaws.com';

type EventCardProps = {
    event: {
        id: number;
        title: string;
        image: string | null;
        venue: string;
        date: string;
        time: string;
        price: number;
        isFreeOfCharge: boolean;
    };
};

const EventCard: React.FC<EventCardProps> = ({ event }) => {
    const navigate = useNavigate();
    
    // Fallback image from S3 bucket
    const fallbackImage = `${S3_BUCKET_URL}/events/placeholder-event.jpg`;
    
    // Process the image URL
    const imageUrl = event.image 
        ? event.image.startsWith('http') 
            ? event.image // Already a full URL
            : `${S3_BUCKET_URL}/${event.image}` // Add S3 bucket URL prefix
        : fallbackImage;

    const handleCardClick = () => {
        navigate(`/events/${event.id}`);
    };

    return (
        <Card
            onClick={handleCardClick}
            className={styles.card}
        >
            <CardMedia
                component="img"
                height="180"
                image={imageUrl}
                alt={event.title}
                className={styles.cardMedia}
            />
            <CardContent className={styles.cardContent}>
                <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    className={styles.title}
                >
                    {event.title}
                </Typography>

                <Box className={styles.locationContainer}>
                    <LocationOnIcon className={styles.locationIcon} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.venueText}
                    >
                        {event.venue}
                    </Typography>
                </Box>

                {/* Date & Time Row */}
                <Box className={styles.dateTimeContainer}>
                    {/* Date */}
                    <Box className={styles.dateContainer}>
                        <EventIcon className={styles.dateIcon} />
                        <Typography variant="body2" className={styles.dateText}>
                            {event.date}
                        </Typography>
                    </Box>

                    {/* Time */}
                    <Box className={styles.timeContainer}>
                        <AccessTimeIcon className={styles.timeIcon} />
                        <Typography variant="body2" className={styles.timeText}>
                            {event.time}
                        </Typography>
                    </Box>
                </Box>

                {/* Price */}
                <Box className={styles.priceContainer}>
                    <Typography
                        variant="h6"
                        className={styles.priceText}
                        sx={{
                            color: event.isFreeOfCharge ? 'success.main' : 'inherit',
                            fontWeight: event.isFreeOfCharge ? 'bold' : 'normal',
                        }}
                    >
                        {event.isFreeOfCharge ? 'FREE' : (
                            <>
                                <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 'normal', color: 'text.secondary' }}>From </Box>
                                <Box component="span" sx={{ fontWeight: 'bold', color: '#EC4899' }}>{formatCurrency(event.price)}</Box>
                            </>
                        )}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export default EventCard;
